import { useTranslation } from 'react-i18next'

import {
  AddressForm,
  Alert,
  Button,
  ButtonWithSpinner,
  CardTitle,
  Checkbox,
  deliveryMethods,
  Drawer,
  PickupDateSelector,
  returnStatuses,
  Text,
  TimeRangeSelector,
} from '@reveni-io/reveni-ui'

import useRegions from '@/common/hooks/useRegions/useRegions'
import DiscardLabelsCheckbox from '@/returns/components/DiscardLabelsCheckbox/DiscardLabelsCheckbox'

import DeliveryMethodSelector from './DeliveryMethodSelector/DeliveryMethodSelector'
import useChangeDeliveryMethod from './hooks/useChangeDeliveryMethod/useChangeDeliveryMethod'
import * as Styles from './styles'

const ChangeDeliveryMethodDrawer = ({
  show,
  onClose,
  availableDeliveryMethods,
  currentDeliveryMethod,
  address,
  loading,
  changeDeliveryMethod,
  status,
}) => {
  const { t } = useTranslation()
  const {
    deliveryMethodSelected,
    newAddress,
    specialConstraints,
    selectedPickupDate,
    selectedTimeRange,
    createNewLabel,
    setCreateNewLabel,
    handleAddressChange,
    onChangeDeliveryMethod,
    setSelectedPickupDate,
    setSelectedTimeRange,
    discardLabels,
    setDiscardLabels,
    isFormValid,
  } = useChangeDeliveryMethod({ availableDeliveryMethods, currentDeliveryMethod, currentAddress: address })
  const { getProvincesByCountryCode } = useRegions()

  const showPickupDateSelector = () => {
    return deliveryMethodSelected?.pickup_dates?.length > 0
  }

  const showTimeRangeSelector = () => {
    return deliveryMethodSelected?.time_ranges?.length > 0
  }

  const shouldShowLabelInfo = () => status !== returnStatuses.onHold

  const onAccept = () => {
    const payload = {
      delivery_method_id: deliveryMethodSelected.id,
      address: {
        ...newAddress,
        country: newAddress?.country?.code,
        province: newAddress?.province?.code,
        house_number: deliveryMethodSelected?.requires_house_number ? newAddress?.house_number || '1' : undefined,
      },
      delivery_time_range_id: selectedTimeRange || undefined,
      pickup_date: selectedPickupDate || undefined,
      generate_label: createNewLabel,
      discard_previous_labels: discardLabels,
    }

    changeDeliveryMethod(payload)
  }

  return (
    <Drawer show={show} onClose={onClose} options={{ showHeaderActions: false }}>
      <CardTitle>{t('Cambiar método de devolución')}</CardTitle>
      <Styles.MainContainer>
        <Styles.SelectDeliveryMethodContainer>
          <Text type="bodyMRegular">
            {t(
              'Selecciona el nuevo método de devolución asociado a la devolución. El cambio no afectará al resultado de la transacción. No se aplicarán ni bonificarán costes nuevos al cliente'
            )}
            :
          </Text>
          <DeliveryMethodSelector
            deliveryMethods={availableDeliveryMethods}
            deliveryMethod={deliveryMethodSelected}
            onChange={onChangeDeliveryMethod}
          />
        </Styles.SelectDeliveryMethodContainer>
        {deliveryMethodSelected?.type === deliveryMethods.customerpickup && (
          <>
            <Styles.AddressContainer>
              <Text type="titleS">{t('Dirección')}</Text>
              <Text type="bodyMRegular">
                {t('Indica la dirección (casa/oficina/...) en la que quieres que se recojan los productos a devolver')}:
              </Text>
              <AddressForm
                shippingAddress={newAddress}
                specialConstraints={specialConstraints}
                onChange={handleAddressChange}
                requiresHouseNumber={deliveryMethodSelected?.requires_house_number}
                carrier={deliveryMethodSelected?.carrier_service}
                allowEditProvince
                provinces={getProvincesByCountryCode(address?.country?.code)}
              />
            </Styles.AddressContainer>

            {(showPickupDateSelector() || showTimeRangeSelector()) && (
              <Styles.DateAndTimeRangeContainer>
                <Text type="titleS">{t('Fecha y hora')}</Text>
                <Text type="bodyMRegular">
                  {t('Indica cuándo prefieres que se recojan los productos en la dirección escogida')}:
                </Text>
                <div>
                  {showPickupDateSelector() && (
                    <PickupDateSelector
                      dates={deliveryMethodSelected?.pickup_dates}
                      onSelectDate={setSelectedPickupDate}
                      selectedDate={selectedPickupDate}
                    />
                  )}

                  {showTimeRangeSelector() && (
                    <TimeRangeSelector
                      onSelectTimeRange={setSelectedTimeRange}
                      selectedTimeRange={selectedTimeRange}
                      timeRanges={deliveryMethodSelected?.time_ranges}
                    />
                  )}
                </div>
              </Styles.DateAndTimeRangeContainer>
            )}
          </>
        )}
        <Styles.NewLabelContainer>
          {shouldShowLabelInfo() && (
            <div>
              {deliveryMethodSelected?.carrier_service?.is_connected && (
                <Checkbox
                  value={createNewLabel}
                  onChange={e => setCreateNewLabel(e.target.checked)}
                  label={t('Generar una nueva etiqueta al guardar cambios')}
                />
              )}
              <DiscardLabelsCheckbox checked={discardLabels} onChange={setDiscardLabels} />
            </div>
          )}
          {createNewLabel && (
            <Alert type="info">
              {t('La nueva etiqueta se enviará al cliente por correo electrónico. ¿Quieres continuar?')}
            </Alert>
          )}
          {!deliveryMethodSelected?.carrier_service?.is_connected && shouldShowLabelInfo() && (
            <Alert type="info">
              {t(
                'Se enviará un correo electrónico al cliente con las nuevas instrucciones de devolución. ¿Quieres continuar?'
              )}
            </Alert>
          )}
        </Styles.NewLabelContainer>
        <Styles.ActionsContainer>
          <Button fullWidth variant="outlined" onClick={onClose}>
            {t('Cancelar')}
          </Button>
          <ButtonWithSpinner
            onClick={onAccept}
            fullWidth
            defaultLabel={t('Guardar')}
            disabled={!isFormValid() || loading || !deliveryMethodSelected}
          />
        </Styles.ActionsContainer>
      </Styles.MainContainer>
    </Drawer>
  )
}

export default ChangeDeliveryMethodDrawer
